/**
 * Lexiun JWT token management — shared across every LexIDE client that
 * needs an authenticated Lexiun session (lexserver REST, gateway WS,
 * harn-service). One token, one cache, all clients in sync.
 *
 * Updated 2026-04-30 auth-flow pivot:
 *   - The token is now minted by **lexserver's** `/api/auth/exchange`
 *     endpoint (not lexruntime's). Both LexIDE and LexPortal hit the same
 *     centralized lexserver route.
 *   - The Clerk JWT is sent in the `Authorization: Bearer` header (per
 *     lexserver's `auth-exchange.ts` contract), not in the request body.
 *     The previous dev-only `{ clerkToken: "dev" }` literal is gone.
 *   - Real Clerk session is required. The Clerk React SDK's
 *     `useAuth().getToken()` is plumbed via a module-level resolver
 *     (registered by `<ClerkTokenBridge>` in App.tsx). Dev workflows that
 *     don't run Clerk can paste a dev-login JWT into
 *     `localStorage["lexide.lexiunToken"]` as an escape hatch.
 *
 * Previously lived inside `lexserver-client.ts`; extracted here per
 * `lexide-implementation-plan.md §4.P.X.8 Step 0a` so the forthcoming
 * harn-client can consume the same helper without taking a cycle through
 * lexserver-client's case/client/matter fetchers.
 */

import { useLanguageStore } from "@/stores/useLanguageStore";
import { SUPPORTED_LANGUAGES } from "@/lib/language";

const AUTH_EXCHANGE = "/api/auth/exchange";
const SEED_TOKEN_LS_KEY = "lexide.lexiunToken";

/**
 * Apply the server-resolved locale to the language store. Called after a
 * successful auth-exchange. Resolution: `user ?? org` (user preference wins
 * over org default). Silently ignores unknown tags so server-side enum
 * expansion ahead of the SPA's `SUPPORTED_LANGUAGES` list never causes a
 * blank UI.
 *
 * Exported for unit testing — production callers use it indirectly via the
 * Clerk-exchange success branch in `getToken()`.
 */
export function applyServerLocale(
  serverLocale: { user?: string | null; org?: string | null } | undefined,
): void {
  const candidate = serverLocale?.user ?? serverLocale?.org;
  if (!candidate) return;
  const match = SUPPORTED_LANGUAGES.find((l) => l.value === candidate);
  if (!match) return;
  const current = useLanguageStore.getState().lang;
  if (current === match.value) return;
  useLanguageStore.getState().setLang(match.value);
}

let cachedToken: string | null = null;
let cachedOrgId: string | null = null;
let cachedUserId: string | null = null;
let cachedScopes: string[] | null = null;
let tokenExpiry = 0;

/**
 * Module-level Clerk-token resolver. Set at app boot by
 * `<ClerkTokenBridge>` in App.tsx — it bridges `useAuth().getToken()`
 * (a hook) into a plain function the non-React `getToken()` below can
 * call. `null` when no Clerk session is active (dev-without-Clerk
 * workflows).
 */
let clerkTokenResolver: (() => Promise<string | null>) | null = null;

export function setClerkTokenResolver(
  resolver: (() => Promise<string | null>) | null,
): void {
  clerkTokenResolver = resolver;
  // Invalidating the cache on every resolver swap keeps a stale Lexiun
  // JWT from outliving its source Clerk session (sign-out → re-sign-in
  // mounts a new bridge with a different identity).
  clearAuthCache();
}

/**
 * Decode the `org` + `sub` + `scopes` claims from an unverified JWT
 * payload.
 *
 * Safe because the token was just minted by lexserver's auth exchange
 * on the same origin — we trust it. Used to populate the orgId / userId
 * / scopes caches alongside the token itself, so callers don't pay a
 * second round trip to resolve the caller's identity.
 *
 * `scopes` defaults to `[]` so callers always get an array. Phase 8
 * uses `scopes.includes("operator.admin")` / `"platform.admin"` to
 * gate the org-admin activity-bar icon.
 *
 * Exported for unit testing — the runtime path uses it via `getToken()`.
 */
export function decodeJwtClaims(
  token: string,
): { org: string | null; sub: string | null; scopes: string[] } {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return { org: null, sub: null, scopes: [] };
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { org?: string; sub?: string; scopes?: unknown };
    const scopes = Array.isArray(payload.scopes)
      ? payload.scopes.filter((s): s is string => typeof s === "string")
      : [];
    return {
      org: payload.org ?? null,
      sub: payload.sub ?? null,
      scopes,
    };
  } catch {
    return { org: null, sub: null, scopes: [] };
  }
}

/**
 * Resolve the source Clerk JWT to exchange. Order:
 *   1. Module-level Clerk resolver (real Clerk session, set by ClerkTokenBridge).
 *   2. localStorage["lexide.lexiunToken"] — DIRECT escape hatch for
 *      dev-without-Clerk: paste a dev-login JWT here and `getToken()`
 *      returns it directly without hitting auth-exchange. Useful for
 *      CI smoke tests + emergency operator recovery.
 *   3. Throws — no auth source available.
 */
async function readSourceToken(): Promise<
  { kind: "seed"; token: string } | { kind: "clerk"; token: string } | null
> {
  // Direct seed wins — operator-set escape hatch.
  if (typeof localStorage !== "undefined") {
    const seed = localStorage.getItem(SEED_TOKEN_LS_KEY);
    if (seed && seed.trim() !== "") return { kind: "seed", token: seed };
  }
  if (clerkTokenResolver) {
    const clerk = await clerkTokenResolver();
    if (clerk) return { kind: "clerk", token: clerk };
  }
  return null;
}

/**
 * Get a Lexiun JWT token, exchanging via lexserver's `/api/auth/exchange`
 * if needed. Exported for callers that need the raw token (lexserver-client's
 * authenticated fetchers; the org-admin layout passing a `getToken` callback
 * into `@lexiun/org-admin`; harn-client).
 *
 * SINGLE-FLIGHT: on a cold load every auth-needing hook calls this
 * concurrently — before the guard, each saw an empty cache and fired its OWN
 * exchange (observed: 8× POST /api/auth/exchange per page load), and their
 * follow-up API calls then piled into the same lexserver rate window
 * (100 req/min/sub → the /org/config 429s, 2026-07-13 operator report).
 * Concurrent callers now share one in-flight exchange promise.
 */
let inflightExchange: Promise<string> | null = null;

export async function getToken(): Promise<string> {
  const now = Date.now() / 1000;
  if (cachedToken && tokenExpiry > now + 60) {
    return cachedToken;
  }
  if (inflightExchange) return inflightExchange;
  inflightExchange = doGetToken().finally(() => {
    inflightExchange = null;
  });
  return inflightExchange;
}

async function doGetToken(): Promise<string> {
  const now = Date.now() / 1000;

  const source = await readSourceToken();
  if (!source) {
    throw new Error(
      "lexiun-jwt: no auth source available — sign in via Clerk or set localStorage[\"lexide.lexiunToken\"] to a dev-login JWT",
    );
  }

  // Seed mode: the localStorage value IS the Lexiun JWT — don't round-trip.
  // No server response → applyServerLocale isn't called; the language store
  // keeps whatever bootLang() resolved (stored → navigator → 'pt-BR'). This
  // is intentional: seed mode is a dev/CI escape hatch, and the seed token
  // doesn't carry a server-resolved locale.
  if (source.kind === "seed") {
    cachedToken = source.token;
    const claims = decodeJwtClaims(cachedToken);
    cachedOrgId = claims.org;
    cachedUserId = claims.sub;
    cachedScopes = claims.scopes;
    // Best-effort expiry: read `exp` from the JWT, else default to 1h.
    try {
      const parts = cachedToken.split(".");
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      ) as { exp?: number };
      tokenExpiry = payload.exp ?? now + 3600;
    } catch {
      tokenExpiry = now + 3600;
    }
    return cachedToken;
  }

  // Clerk mode: exchange the Clerk JWT for a Lexiun JWT via lexserver.
  const res = await fetch(AUTH_EXCHANGE, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${source.token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error(`Auth exchange failed: ${res.status}`);
  }

  const body = (await res.json()) as {
    ok?: boolean;
    data?: {
      token: string;
      expiresIn: number;
      expiresAt?: number;
      locale?: { user: string | null; org: string };
    };
  };
  if (!body.data?.token) {
    throw new Error("Auth exchange response missing token");
  }
  cachedToken = body.data.token;
  const claims = decodeJwtClaims(cachedToken);
  cachedOrgId = claims.org;
  cachedUserId = claims.sub;
  cachedScopes = claims.scopes;
  tokenExpiry = now + body.data.expiresIn;
  // Phase A i18n: hydrate the language store from the server's resolved
  // locale (user override falls through to org default). Forward-compatible
  // with older lexserver builds that don't yet return `locale` — the
  // call no-ops when the field is absent.
  applyServerLocale(body.data.locale);
  return cachedToken;
}

/**
 * Resolve the caller's orgId from the Lexiun JWT's `org` claim.
 * Triggers a token exchange if none is cached.
 */
export async function getCurrentOrgId(): Promise<string> {
  if (!cachedOrgId) {
    await getToken();
  }
  if (!cachedOrgId) {
    throw new Error("lexiun-jwt: auth token has no `org` claim");
  }
  return cachedOrgId;
}

/**
 * Resolve the caller's userId from the Lexiun JWT's `sub` claim.
 * Used by the cascade-gated briefing RPCs that pass userId through
 * to the runtime's access gate.
 */
export async function getCurrentUserId(): Promise<string> {
  if (!cachedUserId) {
    await getToken();
  }
  if (!cachedUserId) {
    throw new Error("lexiun-jwt: auth token has no `sub` claim");
  }
  return cachedUserId;
}

/**
 * Resolve the caller's scopes from the Lexiun JWT. Returns `[]` when
 * the claim is absent (rather than throwing) — most consumers want to
 * default to "no permissions" on a malformed/missing scopes claim.
 *
 * Phase 8: `useAuthIdentity` reads scopes via this helper to decide
 * whether to surface the [🏢] org-admin rail icon in the ActivityBar.
 */
export async function getCurrentScopes(): Promise<string[]> {
  if (cachedScopes === null) {
    await getToken();
  }
  return cachedScopes ?? [];
}

/**
 * SYNCHRONOUS, non-triggering read of the caller's tenant identity —
 * `"{orgId}:{userId}"`, or `null` when either claim is not yet cached.
 *
 * Deliberately distinct from `getCurrentOrgId`/`getCurrentUserId`: those are
 * async and will START A TOKEN EXCHANGE to answer. This one never does, because
 * its callers run inside zustand's `persist` storage hooks, which are
 * synchronous and execute at module-hydration time — before auth has resolved
 * on a cold load.
 *
 * ⚠ `null` means **"cannot tell yet"**, NOT "no tenant". It is a THIRD STATE
 * (D-67) and callers must not collapse it into either identity. The one caller
 * that acts on it — `tenant-scoped-storage.ts` — refuses the cached data on
 * `null`, which is correct THERE for the reason D-95 gives (a cache has no
 * enforcing layer beneath it), and is not a licence to treat `null` as denial
 * anywhere a user-visible claim is made from it.
 */
export function peekTenantKey(): string | null {
  if (!cachedOrgId || !cachedUserId) return null;
  return `${cachedOrgId}:${cachedUserId}`;
}

/** Clear cached token (e.g., on logout or Clerk identity swap). */
export function clearAuthCache(): void {
  cachedToken = null;
  cachedOrgId = null;
  cachedUserId = null;
  cachedScopes = null;
  tokenExpiry = 0;
  // Detach any in-flight exchange: callers already holding the promise keep
  // it (their request was made under the old identity anyway), but the NEXT
  // getToken() must start a fresh exchange under the new identity instead of
  // adopting the stale one.
  inflightExchange = null;
}
